(() => {
  "use strict";

  const SELECTOR_TIMELINE_ROWS = "[data-timeline-rows]";
  const SELECTOR_TIMELINE_ROW = "[data-timeline-row]";
  const SELECTOR_TIMELINE_ROW_CLASS = ".timeline__row";
  const SELECTOR_TIMELINE_ROW_EDITOR = "[data-timeline-row-editor]";
  const SELECTOR_TIMELINE_BUTTON = "[data-timeline-button]";
  const SELECTOR_BLOCK_SCROLL = "[data-block-scroll]";
  const CLASS_IS_SELECTED = "is-selected";
  const SELECTOR_TABS = "[data-tabs-id]";
  const SELECTOR_LIST = "[data-list-id]";

  if (!customElements.get("timeline-component")) {
    customElements.define("timeline-component", class extends HTMLElement {
      constructor() {
        super();
        this.rows = this.querySelectorAll(SELECTOR_TIMELINE_ROW);
        if(this.rows.length === 0) {
          this.rows = this.querySelectorAll(SELECTOR_TIMELINE_ROW_CLASS);
          this.rows[0]?.classList.add(CLASS_IS_SELECTED);
        }

        // Skip initialization if less than 2 rows
        if (this.rows.length < 2) return;

        this.rowsWidth = 0;
        this.rowsHeight = [];
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        this.holderHeight = this.offsetHeight;
        this.buttons = this.querySelectorAll(SELECTOR_TIMELINE_BUTTON);
        this.rowsHolder = this.querySelector(SELECTOR_TIMELINE_ROWS);
        this.requestAnimation = null;
        this.isDesktopView = !window.theme.isMobile();
        this.isScrollEnabled = false;
        this.scrollEvent = (event) => this.scrollEvents(event);
        this.resizeEvent = () => this.resizeEvents()
      }

      connectedCallback() {
        this.calculateRowsDimensions();
        this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

        if (this.isDesktopView && !this.isScrollEnabled) {
          this.isScrollEnabled = true;
          document.addEventListener("theme:scroll", this.scrollEvent);
        }

        this.rowsHolder.addEventListener("scroll", this.scrollEvent);
        document.addEventListener("theme:resize:width", this.resizeEvent);

        if (this.buttons.length) {
          this.buttons.forEach((button, index) => {
            button.addEventListener("click", (event) => {
              event.preventDefault();
              const row = event.currentTarget.closest(SELECTOR_TIMELINE_ROW);

              if (row) {
                if (window.theme.isMobile()) {
                  // Mobile scrolling
                  const padding = 16;
                  const leftPosition = row.offsetLeft - padding;
                  this.rowsHolder.scrollTo({
                    left: leftPosition,
                    behavior: "smooth"
                  });
                } else {
                  // Desktop scrolling
                  const maxHeight = Math.max(...this.rowsHeight);
                  const rowHeight = this.holderHeight / this.rows.length;
                  const holderTopPosition = this.getBoundingClientRect().top;
                  
                  // Calculate position to scroll to
                  const scrollToPosition = (index > 0 && maxHeight < rowHeight ? 
                    rowHeight * index + holderTopPosition : 
                    holderTopPosition) + 1;
                  
                  window.theme.scrollTo(scrollToPosition);
                }
              }
            });
          });
        }
      }

      resizeEvents() {
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        this.holderHeight = this.offsetHeight;
        this.calculateRowsDimensions();
        this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

        const isDesktop = !window.theme.isMobile();

        if (isDesktop && !this.isScrollEnabled) {
          this.isScrollEnabled = true;
          document.addEventListener("theme:scroll", this.scrollEvent);
        } else if (!isDesktop && this.isScrollEnabled) {
          this.isScrollEnabled = false;
          document.removeEventListener("theme:scroll", this.scrollEvent);
        }
      }

      scrollEvents(event) {
        if (!this.requestAnimation) {
          const target = event.currentTarget;
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(target));
        }
      }

      removeAnimationFrame() {
        if (this.requestAnimation) {
          cancelAnimationFrame(this.requestAnimation);
          this.requestAnimation = null;
        }
      }

      calculateRowsDimensions() {
        if (this.rows.length) {
          this.rowsHeight = [];
          this.rowsWidth = 0;
          
          let totalHeight = 0;

          this.rows.forEach((row, index) => {
            this.rowsHeight.push(row.offsetHeight);
            this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

            if (window.Shopify.designMode) {
              const rowEditors = this.querySelectorAll(SELECTOR_TIMELINE_ROW_EDITOR);
              
              if (rowEditors.length) {
                totalHeight += row.offsetHeight;
                
                if (rowEditors[index]) {
                  rowEditors[index].style.setProperty("--row-height-min", `${row.offsetHeight}px`);
                }
                
                if (rowEditors[index + 1]) {
                  rowEditors[index + 1].style.setProperty("--row-top-mobile", `${totalHeight}px`);
                }
              }
            }
          });
        }
      }

      calculatePosition(target = null) {
        this.removeAnimationFrame();
        
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        const isDesktop = !window.theme.isMobile();
        const rowHeight = this.holderHeight / this.rows.length;
        let currentTop = this.holderTop;
        const windowHeight = window.innerHeight;
        const scrolled = window.scrollY + 1.5 * windowHeight;
        let percentComplete = 0;

        if (!isDesktop && target) {
          const scrollLeft = target.scrollLeft - 16;
          const holderWidth = target.offsetWidth;
          percentComplete = (scrollLeft / (this.rowsWidth - holderWidth)) * 100;
          this.style.setProperty("--percent-mobile", `${percentComplete}%`);
        }

        if (isDesktop && (scrolled < this.holderTop || scrolled > this.holderTop + this.holderHeight + windowHeight)) {
          return;
        }

        this.rows.forEach((row, index) => {
          if (index > 0) {
            const rowPercent = (row.offsetLeft / this.rowsWidth) * 100;
            let isVisible = percentComplete > rowPercent;

            if (isDesktop) {
              isVisible = currentTop + rowHeight < scrolled;
              
              if (currentTop - rowHeight < scrolled) {
                const previousRow = row.previousElementSibling;
                if (previousRow) {
                  previousRow.style.setProperty(
                    "--percent-desktop", 
                    ((scrolled - currentTop) / rowHeight) * 100 + "%"
                  );
                }
              }
            }

            row.classList.toggle(CLASS_IS_SELECTED, isVisible);
          }
          
          currentTop += rowHeight;
        });
      }

      disconnectedCallback() {
        if (this.isScrollEnabled) {
          this.isScrollEnabled = false;
          document.removeEventListener("theme:scroll", this.scrollEvent);
        }
        
        this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
        document.removeEventListener("theme:resize:width", this.resizeEvent);
      }

      onBlockSelect(event) {
        const scrollContainer = this.querySelector(SELECTOR_BLOCK_SCROLL);
        
        if (scrollContainer) {
          const selectedElement = event.srcElement;
          const selectedIndex = [...selectedElement.parentElement.children].indexOf(selectedElement);
          const targetRow = this.rows[selectedIndex];
          
          if (targetRow) {
            scrollContainer.scrollTo({
              top: 0,
              left: targetRow.offsetLeft,
              behavior: "smooth"
            });
          }
        }
      }
    });
  }

  if(!customElements.get("timeline-tabs")) {
    customElements.define("timeline-tabs", class extends HTMLElement {
      constructor() {
        super();
        this.id = this.getAttribute("data-tabs-id");
        this.list = document.querySelector(`[data-list-id="${this.id}"]`);
        this.blocks = this.list.querySelectorAll(".timeline-block__body");
        this.tabsData = this.id && window.tabs ? window.tabs[this.id] : [];
        this.loading = true
        this.isMobile = window.innerWidth < 1024;
        this.timelineContainer = this.closest('.timeline-container');

      }

      connectedCallback() {
        this.init();
        this.handleTabsClick();
      }

      init() {
        const tabsClass = 'timeline__tabs__item text-sm py-4 px-6 border border-brown rounded-[100px] cursor-pointer [&.active]:border-primary whitespace-nowrap [&_p]:!mb-0 ';
        if(this.loading) {
          this.innerHTML = this.tabsData.map((tab, index) => `<div class="${tabsClass} ${index === 0 && 'active'}" data-timeline-tab="${tab.id}">${tab.title}</div>`).join("");
          this.loading = false;
          this.blocks[0]?.classList?.add("active");
          this.list.classList.remove("loading");
        }
      }

      handleTabsClick() {
        const tabs = this.querySelectorAll("[data-timeline-tab]");
        tabs.forEach((tab) => {
          tab.addEventListener("click", () => {
            const id = tab.getAttribute("data-timeline-tab");
            tabs.forEach((tab) => {
              tab.classList.remove("active");
            });
            this.blocks.forEach((block) => {
              block.classList.remove("active");
            });
            tab.classList.add("active");
            const block = this.list.querySelector(`#${id}`);
            block.classList.add("active");
            const timelineComponent = block.querySelector("timeline-component");
            if (timelineComponent) {
    
              timelineComponent.calculateRowsDimensions();
              timelineComponent.calculatePosition();
            }
          });
        });
      }
    });
  }
})();
